import * as React from 'react';
import { cn } from '../cn';

export function Kbd({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
  return <kbd className={cn('inline-flex items-center h-5 px-1.5 rounded-[var(--radius-xs)] bg-fill-2 text-text-2 text-[11px] font-medium font-[var(--font-mono)] shadow-[inset_0_-1px_0_var(--separator)]', className)} {...props} />;
}

export interface MoneyTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  amount: number | string | null | undefined;
  currency?: string;
  locale?: string;
  colorNegative?: boolean;
  signed?: boolean;
  muted?: boolean;
}
export function MoneyText({ amount, currency = 'CAD', locale = 'en-CA', colorNegative, signed, muted, className, ...props }: MoneyTextProps) {
  const n = typeof amount === 'string' ? Number(amount) : (amount ?? 0);
  let text: string;
  try {
    text = new Intl.NumberFormat(locale, { style: 'currency', currency, signDisplay: signed ? 'exceptZero' : 'auto' }).format(n);
  } catch {
    text = `${currency} ${n.toFixed(2)}`;
  }
  return (
    <span className={cn('tabular', colorNegative && n < 0 && 'text-red', signed && n > 0 && 'text-green', muted && 'text-text-2', className)} {...props}>
      {text}
    </span>
  );
}

export interface DateTextProps extends React.HTMLAttributes<HTMLTimeElement> {
  value: string | Date | null | undefined;
  format?: 'date' | 'datetime' | 'time' | 'relative' | 'weekday';
  timeZone?: string;
  locale?: string;
  fallback?: string;
}
export function formatDateValue(value: string | Date | null | undefined, format: DateTextProps['format'] = 'date', timeZone?: string, locale = 'en-CA'): string {
  if (!value) return '';
  const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = typeof value === 'string' ? new Date(isDateOnly ? value + 'T00:00:00' : value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  const tz = isDateOnly ? undefined : timeZone;
  switch (format) {
    case 'datetime':
      return new Intl.DateTimeFormat(locale, { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
    case 'time':
      return new Intl.DateTimeFormat(locale, { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(d);
    case 'weekday':
      return new Intl.DateTimeFormat(locale, { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }).format(d);
    case 'relative': {
      const diff = d.getTime() - Date.now();
      const abs = Math.abs(diff);
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      if (abs < 60_000) return 'just now';
      if (abs < 3_600_000) return rtf.format(Math.round(diff / 60_000), 'minute');
      if (abs < 86_400_000) return rtf.format(Math.round(diff / 3_600_000), 'hour');
      if (abs < 30 * 86_400_000) return rtf.format(Math.round(diff / 86_400_000), 'day');
      return new Intl.DateTimeFormat(locale, { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' }).format(d);
    }
    default:
      return new Intl.DateTimeFormat(locale, { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  }
}
export function DateText({ value, format = 'date', timeZone, locale, fallback = '—', className, ...props }: DateTextProps) {
  const text = formatDateValue(value, format, timeZone, locale);
  if (!text) return <span className={cn('text-text-3', className)}>{fallback}</span>;
  const iso = value instanceof Date ? value.toISOString() : String(value);
  return (
    <time dateTime={iso} suppressHydrationWarning className={cn('tabular whitespace-nowrap', className)} {...props}>
      {text}
    </time>
  );
}

export function Separator({ className, vertical, ...props }: React.HTMLAttributes<HTMLDivElement> & { vertical?: boolean }) {
  return <div role="separator" className={cn(vertical ? 'w-px self-stretch bg-separator' : 'h-px w-full bg-separator', className)} {...props} />;
}

export function Spinner({ className, size = 16 }: { className?: string; size?: number }) {
  return (
    <svg className={cn('ui-spin text-text-3', className)} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-label="Loading" role="status">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ui-shimmer rounded-[var(--radius-sm)] h-4 w-full', className)} aria-hidden {...props} />;
}

export function SectionTitle({ className, children, action }: { className?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className={cn('flex items-center justify-between gap-3 mb-3', className)}>
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-text-2">{children}</h2>
      {action}
    </div>
  );
}
